import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useT } from '../i18n';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';

export default function LoginPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const { login, user } = useAuth();
    const t = useT();
    const googleSuccessMessage = t('auth.success_google');
    const googleFailureMessage = t('auth.fail_google');

    const [mode, setMode] = useState('login');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [name, setName] = useState('');
    const [birthdate, setBirthdate] = useState('');
    const [registerConfirm, setRegisterConfirm] = useState('');
    const [loading, setLoading] = useState(false);
    const [shouldRedirect, setShouldRedirect] = useState(false);
    const [googleError, setGoogleError] = useState('');
    const googleButtonRef = useRef(null);

    const from = location.state?.from && location.state.from !== '/entrar' ? location.state.from : '/';

    // Redirect when user becomes authenticated (after login() updates context)
    useEffect(() => {
        if (shouldRedirect && user) {
            navigate(from, { replace: true });
        }
    }, [shouldRedirect, user, from, navigate]);

    // If already authenticated and the user lands on /entrar, send home
    useEffect(() => {
        if (user && !shouldRedirect) {
            navigate(from, { replace: true });
        }
    }, [from, navigate, shouldRedirect, user]);

    useEffect(() => {
        let cancelled = false;

        const loadGoogleScript = () => new Promise((resolve, reject) => {
            if (window.google?.accounts?.id) { resolve(); return; }
            const existing = document.querySelector('script[src="https://accounts.google.com/gsi/client"]');
            if (existing) {
                existing.addEventListener('load', resolve, { once: true });
                existing.addEventListener('error', reject, { once: true });
                return;
            }
            const script = document.createElement('script');
            script.src = 'https://accounts.google.com/gsi/client';
            script.async = true;
            script.defer = true;
            script.onload = resolve;
            script.onerror = reject;
            document.head.appendChild(script);
        });

        const initializeGoogle = async () => {
            try {
                const configResponse = await api.get('/config');
                const clientId = configResponse.data.google_client_id;
                if (!clientId) {
                    setGoogleError('Google encara no està configurat');
                    return;
                }
                await loadGoogleScript();
                if (cancelled || !googleButtonRef.current) return;
                window.google.accounts.id.initialize({
                    client_id: clientId,
                    callback: async ({ credential }) => {
                        setLoading(true);
                        try {
                            const response = await api.post('/auth/google/session', { credential });
                            login(response.data.token, response.data.user);
                            toast.success(googleSuccessMessage);
                            navigate(from, { replace: true });
                        } catch (error) {
                            toast.error(error.response?.data?.detail || googleFailureMessage);
                        } finally {
                            setLoading(false);
                        }
                    },
                });
                googleButtonRef.current.replaceChildren();
                window.google.accounts.id.renderButton(googleButtonRef.current, {
                    theme: 'filled_black',
                    size: 'large',
                    shape: 'rectangular',
                    text: 'continue_with',
                    width: Math.min(360, googleButtonRef.current.clientWidth || 360),
                });
            } catch {
                if (!cancelled) setGoogleError('No s’ha pogut carregar Google');
            }
        };

        initializeGoogle();
        return () => { cancelled = true; };
    }, [from, googleFailureMessage, googleSuccessMessage, login, navigate]);

    const onSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const url = mode === 'login' ? '/auth/login' : '/auth/register';
            if (mode === 'register') {
                // Client-side password policy: min 6, at least one uppercase, at least one symbol
                const pw = password || '';
                if (pw.length < 6) {
                    toast.error('La contrasenya ha de tenir com a mínim 6 caràcters');
                    setLoading(false);
                    return;
                }
                if (!/[A-Z]/.test(pw)) {
                    toast.error('La contrasenya ha de contenir almenys una lletra majúscula');
                    setLoading(false);
                    return;
                }
                if (!/[^A-Za-z0-9]/.test(pw)) {
                    toast.error('La contrasenya ha de contenir almenys un símbol');
                    setLoading(false);
                    return;
                }
                if (password !== registerConfirm) {
                    toast.error("Les contrasenyes no coincideixen");
                    setLoading(false);
                    return;
                }
            }
                // Ensure birthdate provided (required) and minimum age of 13
                if (mode === 'register') {
                    if (!birthdate) {
                        toast.error('Cal indicar la data de naixement');
                        setLoading(false);
                        return;
                    }
                    const bd = new Date(birthdate);
                    const today = new Date();
                    let ageYears = today.getFullYear() - bd.getFullYear();
                    if (today.getMonth() < bd.getMonth() || (today.getMonth() === bd.getMonth() && today.getDate() < bd.getDate())) ageYears -= 1;
                    if (ageYears < 13) {
                        toast.error('Cal tenir com a mínim 13 anys per crear un compte a Albambam');
                        setLoading(false);
                        return;
                    }
                }
            const body = mode === 'login' ? { email, password } : { email, password, name, birthdate };
            if (mode === 'register') {
                try {
                    const ref = localStorage.getItem('alb_ref');
                    if (ref) body.ref = ref;
                } catch {}
            }
            const r = await api.post(url, body);
            login(r.data.token, r.data.user);
            toast.success(mode === 'login' ? t('auth.success_login') : t('auth.success_register'));
            setShouldRedirect(true);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Error');
        } finally { setLoading(false); }
    };

    return (
        <main className="mx-auto max-w-[1400px] px-3 sm:px-5 lg:px-8 py-6 md:py-16 pb-24 md:pb-16" data-testid="login-page">
            <div className="max-w-md mx-auto">
                <Link to="/" className="inline-flex items-center gap-2 mb-6">
                    <img src="/logo.jpg" alt="Albambam" className="h-10 w-10 rounded-lg" />
                    <span className="font-display font-bold text-2xl alb-text-gradient">Albambam</span>
                </Link>
                <Card className="p-6 bg-[color:var(--alb-surface)] border-[color:var(--alb-hairline)]">
                    <Tabs value={mode} onValueChange={setMode}>
                        <TabsList className="grid grid-cols-2 w-full bg-[color:var(--alb-surface-2)]">
                            <TabsTrigger value="login" data-testid="tab-login">{t('auth.login_tab')}</TabsTrigger>
                            <TabsTrigger value="register" data-testid="tab-register">{t('auth.register_tab')}</TabsTrigger>
                        </TabsList>
                        <TabsContent value="login" className="mt-4">
                            <form onSubmit={onSubmit} className="space-y-3">
                                <div>
                                    <Label htmlFor="email">{t('auth.email')}</Label>
                                    <Input data-testid="login-email-input" id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="bg-[color:var(--alb-ink-2)] border-[color:var(--alb-hairline)]" />
                                </div>
                                <div>
                                    <Label htmlFor="password">{t('auth.password')}</Label>
                                    <Input data-testid="login-password-input" id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="bg-[color:var(--alb-ink-2)] border-[color:var(--alb-hairline)]" />
                                </div>
                                <Button data-testid="login-submit-button" disabled={loading} className="w-full bg-[color:var(--alb-sun)] text-[color:var(--alb-ink)] hover:bg-[color:var(--alb-tangerine)]">{loading ? t('auth.submit_login_loading') : t('auth.submit_login')}</Button>
                                <div className="text-right text-sm mt-2">
                                    <button type="button" className="text-[color:var(--alb-link)] underline" onClick={async () => {
                                        const e = window.prompt('Introdueix el teu correu per recuperar la contrasenya:');
                                        if (!e) return;
                                        try {
                                            const res = await api.post('/auth/forgot-password', { email: e });
                                            if (res.data && res.data.reset_token) {
                                                toast.success('Token (dev): ' + res.data.reset_token);
                                            } else {
                                                toast.success('Si existeix el compte, s\'ha enviat un correu de recuperació');
                                            }
                                        } catch (err) {
                                            toast.error(err.response?.data?.detail || 'Error');
                                        }
                                    }}>He oblidat la contrasenya</button>
                                </div>
                            </form>
                        </TabsContent>
                        <TabsContent value="register" className="mt-4">
                            <form onSubmit={onSubmit} className="space-y-3">
                                <div>
                                    <Label htmlFor="name">{t('auth.name')}</Label>
                                    <Input data-testid="register-name-input" id="name" required value={name} onChange={(e) => setName(e.target.value)} className="bg-[color:var(--alb-ink-2)] border-[color:var(--alb-hairline)]" />
                                </div>
                                <div>
                                    <Label htmlFor="r-email">{t('auth.email')}</Label>
                                    <Input data-testid="register-email-input" id="r-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="bg-[color:var(--alb-ink-2)] border-[color:var(--alb-hairline)]" />
                                </div>
                                <div>
                                    <Label htmlFor="r-password">{t('auth.password')}</Label>
                                    <Input data-testid="register-password-input" id="r-password" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} className="bg-[color:var(--alb-ink-2)] border-[color:var(--alb-hairline)]" />
                                </div>
                                <div>
                                    <Label htmlFor="r-confirm">Confirma la contrasenya</Label>
                                    <Input data-testid="register-confirm-input" id="r-confirm" type="password" required minLength={6} value={registerConfirm} onChange={(e) => setRegisterConfirm(e.target.value)} className="bg-[color:var(--alb-ink-2)] border-[color:var(--alb-hairline)]" />
                                </div>
                                <div>
                                    <Label htmlFor="r-birthdate">Data de naixement (obligatòria)</Label>
                                    <Input id="r-birthdate" type="date" required value={birthdate} onChange={(e) => setBirthdate(e.target.value)} className="bg-[color:var(--alb-ink-2)] border-[color:var(--alb-hairline)]" />
                                    <p className="mt-1 text-xs text-[color:var(--alb-text-muted)]">Cal tenir com a mínim 13 anys per registrar-te. La retirada de fons de la cartera requereix ser major de 18 anys.</p>
                                </div>
                                
                                <Button data-testid="register-submit-button" disabled={loading} className="w-full bg-[color:var(--alb-sun)] text-[color:var(--alb-ink)] hover:bg-[color:var(--alb-tangerine)]">{loading ? t('auth.submit_register_loading') : t('auth.submit_register')}</Button>
                            </form>
                        </TabsContent>
                    </Tabs>

                    <div className="my-4 flex items-center gap-3">
                        <div className="flex-1 h-px bg-[color:var(--alb-hairline)]" />
                        <span className="text-xs text-[color:var(--alb-text-muted)]">{t('auth.or')}</span>
                        <div className="flex-1 h-px bg-[color:var(--alb-hairline)]" />
                    </div>
                    <div ref={googleButtonRef} data-testid="google-login-button" className="flex min-h-11 w-full justify-center" />
                    {googleError && <p className="mt-2 text-center text-xs text-[color:var(--alb-text-muted)]">{googleError}</p>}
                </Card>
            </div>
        </main>
    );
}
